import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { waitingPatch } from './asyncPlay';
import {
  ref as dbRef, onValue, push, set, update, get, remove, onDisconnect,
} from 'firebase/database';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { db, auth, googleProvider } from '../firebase';
import {
  createGameState, dispatch as engineDispatch, playAITurn, resolveAIOwedPendingChoices, revealsNewInformation,
  DIFFICULTY_PRESETS, DEFAULT_BUY_WEIGHTS,
} from '../engine/index';
import { recordFinishedGameOnce } from './userStats';
import { BOARD_SIDES } from '../engine/constants';
import { TUTORIAL_SEED } from '../tutorial/thunderstoneSteps';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { readLocalGame, writeLocalGame, clearLocalGame } from './localGameStorage';
import {
  ensureSiteUserRecord, watchSiteUserStatus, watchAllSiteUsers, setSiteUserStatus,
} from './siteAccess';
import { readLastSignInEmail, writeLastSignInEmail } from './lastSignIn';
import { updateCachedApproval, readCachedApproval } from './siteAccessCache';

// How long initAuth waits for the LIVE siteUsers/{uid} subscription before
// falling back to a cached approval (see siteAccessCache.js) instead of
// leaving the user stuck on App.vue's spinner - Matt: play local
// hotseat/solo-vs-AI fully offline (e.g. on a flight) after having visited
// the site once while online. "A few seconds is plenty" per the design -
// long enough that a normal, merely-slow connection still resolves for
// real first, short enough that a genuinely offline device doesn't sit on
// a spinner for any meaningful time.
const OFFLINE_FALLBACK_TIMEOUT_MS = 4000;
// Bounds the "create siteUsers/{uid} if missing" write below - kept
// noticeably shorter than OFFLINE_FALLBACK_TIMEOUT_MS so, even fully
// offline, the real watchSiteUserStatus subscription is already attached
// (see initAuth) well before the offline-approval fallback itself fires.
const ENSURE_RECORD_TIMEOUT_MS = 1500;

// Runs AI turns until control returns to a human (or the game ends) - a
// player like Scabbark's "take another turn" Spoils could otherwise leave an
// AI player active again immediately.
// Looks up the AI-difficulty weights for a player - the `|| DEFAULT_BUY_WEIGHTS`
// fallback only matters for a local game saved (localStorage) or a remote
// game synced (RTDB) by a build from before `difficulty` existed on the
// player object; both persistence paths do a raw whole-state JSON
// round-trip with no field allow-listing, so an old blob's player objects
// simply lack the field rather than having some other stale value.
function aiWeightsFor(player) {
  return DIFFICULTY_PRESETS[player.difficulty] || DEFAULT_BUY_WEIGHTS;
}

function runAiTurnsToNextHuman(state) {
  let s = state;
  while (!s.meta.gameOver) {
    const active = s.players[s.meta.activePlayerIndex];
    if (!active.isAI) break;
    s = playAITurn(s, active.id, aiWeightsFor(active));
  }
  return s;
}

// Keep references to active RTDB listeners so we can detach on rebind - same
// pattern as full-table's src/store/index.js.
const unsubscribers = {};
const detach = (key) => {
  if (unsubscribers[key]) {
    unsubscribers[key]();
    delete unsubscribers[key];
  }
};


// Async play (asyncPlay.js, 2026-10-04): a remote room with meta.async waits
// for people instead of anyone playing on; after every state write it says
// who it waits on. The active seat; an AI seat waits on the lowest-seat
// human, who drives it when they open the room.
const asyncAdapter = {
  waitingOnOf: (g) => {
    const active = g?.players?.[g.meta.activePlayerIndex];
    if (!active) return [];
    if (!active.isAI) return [active.id];
    const humans = (g.players || []).filter((p) => !p.isAI);
    return humans.length ? [humans[0].id] : [];
  },
  isOver: (g) => Boolean(g?.meta?.gameOver),
};
const recordWaiting = (gameId, gameState, meta) => {
  if (!db || !gameId || !meta?.async || !gameState) return;
  update(dbRef(db, `games/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export default createStore({
  state: {
    user: null, // { uid, displayName, email, photoURL } or null
    authReady: false, // becomes true after first onAuthStateChanged
    gameState: null, // the engine's GameState - shared by local AND remote play once bound
    remoteGameId: null, // null when playing locally (hotseat/solo-vs-AI)
    remoteGameMeta: null, // { hostUid, status, roomCode, boardSide, playerSlots } while remoteGameId is set
    remoteError: null,
    // Matt reported this directly: "Be cool if there was an indicator if
    // somebody is actively in the game like if they have the screen open
    // or not in the sidebar where I see their information." { uid: true }
    // for whoever currently has this room's connection open - see
    // bindRemoteGame's own onDisconnect() wiring for how a closed tab/lost
    // connection clears itself automatically without any explicit
    // "leaving" action.
    remotePresence: {},
    // Stack of gameState snapshots taken *before* each undoable action this
    // turn, most-recent-last. Purely local UI state - deliberately never
    // synced to Firebase (see bindRemoteGame): each client only ever needs
    // to undo its own not-yet-locked-in actions. Cleared whenever the turn
    // ends or an action reveals information that can't be un-seen (see
    // submitAction and engine/state/informationReveal.js).
    undoStack: [],
    // Matt: "build in tutorials... interactive, not just rulebook dumps."
    // True only while a tutorial-launched game is active - GameBoard.vue
    // reads this to mount TutorialOverlay.vue. Deliberately NOT persisted
    // (see startTutorial/exitTutorial below, which skip writeLocalGame) -
    // a tutorial run is disposable, not a "resume where I left off" game.
    tutorialActive: false,
    // Site-wide login+approval gate (see store/siteAccess.js) - shared
    // across every Around Table Round game via one Firebase project.
    siteUser: null, // { email, displayName, status, isAdmin, requestedAt } or null (not yet loaded/no record)
    siteUserReady: false, // becomes true once the first siteUsers/{uid} read resolves
    allSiteUsers: {}, // admin-only; { uid: siteUserRecord } - populated only when isAdmin
  },

  getters: {
    isLoggedIn: (state) => Boolean(state.user),
    isApproved: (state) => state.siteUser?.status === 'approved',
    isSiteAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    pendingSiteUsers: (state) => Object.entries(state.allSiteUsers || {})
      .filter(([, u]) => u.status === 'pending')
      .map(([uid, u]) => ({ uid, ...u })),
    activePlayer: (state) => (state.gameState
      ? state.gameState.players[state.gameState.meta.activePlayerIndex]
      : null),
    // For local play (hotseat/solo) everyone shares one screen, so there's no
    // "not your turn" concept at the UI level. For remote play, gate
    // interactive controls to the player whose uid matches the active seat.
    isMyTurn: (state, getters) => {
      if (!state.remoteGameId) return true;
      return Boolean(state.user) && getters.activePlayer && getters.activePlayer.id === state.user.uid;
    },
    canUndo: (state) => state.undoStack.length > 0,
  },

  mutations: {
    setUser: (state, user) => { state.user = user; },
    setAuthReady: (state, v) => { state.authReady = v; },
    setGameState: (state, gameState) => { state.gameState = gameState; },
    setRemoteGameId: (state, id) => { state.remoteGameId = id; },
    setRemoteGameMeta: (state, meta) => { state.remoteGameMeta = meta; },
    setRemoteError: (state, err) => { state.remoteError = err; },
    setRemotePresence: (state, presence) => { state.remotePresence = presence || {}; },
    setUndoStack: (state, stack) => { state.undoStack = stack; },
    setTutorialActive: (state, v) => { state.tutorialActive = v; },
    setSiteUser: (state, siteUser) => { state.siteUser = siteUser; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setAllSiteUsers: (state, users) => { state.allSiteUsers = users; },
  },

  actions: {
    initAuth({ commit }) {
      if (!auth) { commit('setAuthReady', true); commit('setSiteUserReady', true); return; } // no Firebase project configured yet
      detach('siteUser');
      detach('allSiteUsers');
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        // An anonymous session exists only so a signed-out bug report can
        // satisfy the inbox's auth != null rule (utils/bugReports.js has
        // always minted one). It is NOT a player: without this filter, filing
        // a bug while signed out left the app looking signed in as nobody.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setAuthReady', true);
          commit('setSiteUser', null);
          commit('setSiteUserReady', true);
          return;
        }
        commit('setUser', user
          ? { uid: user.uid, displayName: user.displayName, email: user.email, photoURL: user.photoURL }
          : null);
        if (user?.email) writeLastSignInEmail(user.email);
        commit('setAuthReady', true);
        detach('siteUser');
        detach('allSiteUsers');
        if (!user) {
          commit('setSiteUser', null);
          commit('setAllSiteUsers', {});
          commit('setSiteUserReady', true);
          return;
        }
        // --- Offline approval fallback (see siteAccessCache.js) ---------
        // Scheduled up front, before anything below that touches the
        // network, so a stalled/offline connection never delays it:
        // Firebase's own get()/onValue() calls have no built-in timeout
        // and can simply hang while offline, which is exactly the
        // situation ("already signed in and approved, now offline") this
        // exists to unstick. Does NOT tear down or replace the real live
        // subscription below - that keeps running in the background
        // regardless, so if the device comes back online and something
        // changed (e.g. access was revoked), it still self-corrects.
        let liveResolved = false;
        const { uid } = user;
        const useCachedApprovalIfStillWaiting = () => {
          if (liveResolved) return;
          const cached = readCachedApproval(uid);
          if (cached) {
            commit('setSiteUser', cached);
            commit('setSiteUserReady', true);
          }
          // No cache -> stays on the spinner/sign-in flow, same as today.
          // Deliberately fails safe: an unconfirmed user with no prior
          // approved snapshot should never be let in just because the
          // network happens to be down right now.
        };
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          useCachedApprovalIfStillWaiting();
        } else {
          setTimeout(useCachedApprovalIfStillWaiting, OFFLINE_FALLBACK_TIMEOUT_MS);
        }

        // Create-if-missing, THEN start watching - avoids a flash of
        // "pending" for a genuinely brand-new sign-in whose write hasn't
        // landed yet (the watcher below would otherwise fire once on
        // null/nonexistent before the record exists). Bounded by a short
        // race, not awaited unconditionally like before: a stalled/offline
        // write here (Firebase's own get()/set() have no built-in timeout)
        // must never hold up starting the real watcher below, which is
        // what the offline fallback above actually depends on being
        // attached at all. On any real network this resolves in well under
        // ENSURE_RECORD_TIMEOUT_MS, so normal online behavior/timing is
        // unchanged - the race only ever actually matters when offline.
        await Promise.race([
          ensureSiteUserRecord(user).catch(() => {}),
          new Promise((resolve) => { setTimeout(resolve, ENSURE_RECORD_TIMEOUT_MS); }),
        ]);
        unsubscribers.siteUser = watchSiteUserStatus(uid, (record) => {
          liveResolved = true;
          commit('setSiteUser', record);
          commit('setSiteUserReady', true);
          updateCachedApproval(uid, record);
          if (record?.isAdmin) {
            detach('allSiteUsers');
            unsubscribers.allSiteUsers = watchAllSiteUsers((users) => commit('setAllSiteUsers', users));
          } else {
            detach('allSiteUsers');
            commit('setAllSiteUsers', {});
          }
        });
      });
    },

    async approveSiteUser(_, uid) {
      await setSiteUserStatus(uid, 'approved');
    },

    async denySiteUser(_, uid) {
      await setSiteUserStatus(uid, 'denied');
    },

    async login() {
      if (!auth) throw new Error('Firebase is not configured (see .env.example).');
      // Not cross-domain SSO (see lastSignIn.js) - just biases/pre-fills
      // Google's account chooser toward whichever account last signed in
      // successfully on this origin, so a repeat sign-in on a
      // multi-account browser doesn't start from a blank chooser.
      const lastEmail = readLastSignInEmail();
      googleProvider.setCustomParameters(lastEmail ? { login_hint: lastEmail } : {});
      markInteractiveSignIn();
      await signInWithPopup(auth, googleProvider);
    },

    async logout({ commit }) {
      if (!auth) return;
      await endSharedSignIn();
      await signOut(auth);
      commit('setUser', null);
    },

    // ---- Local play (hotseat / solo-vs-AI): no Firebase project needed, but
    // still persisted - to the browser's own localStorage rather than a
    // server - so "come back to it later" works the same way it does for a
    // remote game (see localGameStorage.js).

    startLocalGame({ commit }, {
      playerNames, boardSide = BOARD_SIDES.DUNGEON, aiPlayerIndexes = [], aiDifficulties = {}, showLiveScores = false, revealDeckContents = false,
      randomizedVillage = false, synergyPreferredVillage = false, hardDarkness = false, dungeonDeckTargetValue = null, bearerCount = 1, bearerNeverEscapes = false,
      autoEndTurn = true, seed: requestedSeed = null,
    }) {
      const playerConfigs = playerNames.map((name, i) => ({
        id: `p${i + 1}`, name, isAI: aiPlayerIndexes.includes(i), difficulty: aiDifficulties[i] || 'normal',
      }));
      // Matt: "There should be a way to replay a seed. So if you really like
      // a set of cards you can run it back again." An explicitly requested
      // seed (Landing's optional seed field, shown on the game-over screen)
      // wins over the random roll - same seed, same setup, same draws.
      // Math.random() is fine here - this is lobby/UI code, not src/engine/**.
      const seed = requestedSeed ?? Math.floor(Math.random() * 2 ** 31);
      let gameState = createGameState({
        playerConfigs,
        boardSide,
        seed,
        showLiveScores,
        revealDeckContents,
        randomizedVillage,
        synergyPreferredVillage,
        hardDarkness,
        dungeonDeckTargetValue,
        bearerCount,
        bearerNeverEscapes,
        autoEndTurn,
        // Matt asked for this directly, twice: no table-side negotiation
        // happens in an async digital game, so always handing p1 the first
        // turn isn't warranted (rulebook, p.9, leaves the choice open) -
        // see createGameState's own comment.
        randomizeStartingPlayer: true,
      });
      gameState = runAiTurnsToNextHuman(gameState); // in case p1 itself is AI (not currently offered by the UI, but supported)
      commit('setGameState', gameState);
      commit('setUndoStack', []);
      writeLocalGame(gameState);
    },

    endLocalGame({ commit }) {
      commit('setGameState', null);
      clearLocalGame();
    },

    // Matt: "There should be a way to replay a seed. So if you really like a
    // set of cards you can run it back again." / Brian: "I would love to be
    // able to replay a game with all the same cards." Every input
    // createGameState needs is already stored on the finished game itself
    // (meta.seed + every house-rule flag, players' own name/isAI/difficulty),
    // so a rematch is a pure reconstruction: same seed means the same
    // village, the same dungeon deck order, and the same starting player.
    rematchLocalGame({ state, commit }) {
      const finished = state.gameState;
      if (!finished || state.remoteGameId) return;
      const { meta } = finished;
      let gameState = createGameState({
        playerConfigs: finished.players.map(({
          id, name, isAI, difficulty,
        }) => ({
          id, name, isAI, difficulty,
        })),
        boardSide: meta.boardSide,
        seed: meta.seed,
        showLiveScores: meta.showLiveScores,
        revealDeckContents: meta.revealDeckContents,
        randomizedVillage: meta.randomizedVillage,
        synergyPreferredVillage: meta.synergyPreferredVillage,
        hardDarkness: meta.hardDarkness,
        dungeonDeckTargetValue: meta.dungeonDeckTargetValue,
        bearerCount: meta.bearerCount,
        bearerNeverEscapes: meta.bearerNeverEscapes,
        autoEndTurn: meta.autoEndTurn,
        randomizeStartingPlayer: true,
      });
      gameState = runAiTurnsToNextHuman(gameState);
      commit('setGameState', gameState);
      commit('setUndoStack', []);
      writeLocalGame(gameState);
    },

    // Matt: "build in tutorials... interactive, not just rulebook dumps."
    // A fixed seed (TUTORIAL_SEED, thunderstoneSteps.js), not a random
    // one - the tutorial's own step script references what that exact
    // seed deals (an affordable Level 1 hero in every hero stack) to know
    // when the player has actually done what a step asked. Deliberately
    // skips writeLocalGame - unlike a real game, a tutorial run isn't
    // meant to survive a refresh/resume; exiting or finishing it just
    // clears back to the landing page.
    startTutorial({ commit }) {
      let gameState = createGameState({
        playerConfigs: [
          { id: 'p1', name: 'You', isAI: false },
          { id: 'p2', name: 'Computer', isAI: true, difficulty: 'normal' },
        ],
        boardSide: BOARD_SIDES.DUNGEON,
        seed: TUTORIAL_SEED,
      });
      gameState = runAiTurnsToNextHuman(gameState);
      commit('setGameState', gameState);
      commit('setUndoStack', []);
      commit('setTutorialActive', true);
    },

    // "Skip tutorial" - bails out entirely, back to the landing page,
    // same as endLocalGame. Distinct from finishTutorial below.
    exitTutorial({ commit }) {
      commit('setTutorialActive', false);
      commit('setGameState', null);
      commit('setUndoStack', []);
    },

    // Reaching the tutorial's last step, instead - the walkthrough is
    // done, but there's no reason to throw away the game it was just
    // played on. Just drops the overlay and starts persisting from here
    // on, same as any other local game (writeLocalGame wasn't called
    // during the tutorial itself - see startTutorial's own comment).
    finishTutorial({ commit, state }) {
      commit('setTutorialActive', false);
      writeLocalGame(state.gameState);
    },

    // Restores whatever local game (if any) was last saved - called once at
    // app boot (see main.js) so a full browser restart resumes it, and again
    // whenever a remote game is unbound (below) so leaving a remote game
    // doesn't strand an in-progress local game that's still sitting in
    // storage.
    loadLocalGame({ commit }) {
      commit('setGameState', readLocalGame());
    },

    // ---- Remote play: client-authoritative full-snapshot sync over Firebase
    // Realtime Database (see plan, "Multiplayer sync design"). Every action
    // still goes through the exact same engine.dispatch() as local play -
    // the only difference is where the resulting state gets written.

    async createRemoteGame({ state, dispatch }, {
      boardSide = BOARD_SIDES.DUNGEON, showLiveScores = false, revealDeckContents = false,
      randomizedVillage = false, synergyPreferredVillage = false, hardDarkness = false, dungeonDeckTargetValue = null, bearerCount = 1, bearerNeverEscapes = false,
      autoEndTurn = true, maxPlayers = 2, async: asyncRoom = false,
    }) {
      trackEvent('room-hosted');
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      if (!state.user) throw new Error('Sign in first.');

      const gameRef = push(dbRef(db, 'games'));
      const gameId = gameRef.key;

      let code;
      let attempts = 0;
      do {
        code = generateRoomCode(attempts);
        attempts += 1;
        // eslint-disable-next-line no-await-in-loop
      } while (attempts < 10 && (await get(dbRef(db, `gameCodes/${code}`))).exists());

      const meta = {
        hostUid: state.user.uid,
        status: 'lobby',
        async: asyncRoom,
        boardSide,
        showLiveScores,
        revealDeckContents,
        randomizedVillage,
        synergyPreferredVillage,
        hardDarkness,
        dungeonDeckTargetValue,
        bearerCount,
        bearerNeverEscapes,
        autoEndTurn,
        // Rulebook supports 2-5 players (Matt: "expand this to be more
        // than just two players"). Clamped here, not just in the UI -
        // this is the actual system boundary a malformed/bypassed client
        // request would hit.
        maxPlayers: Math.min(Math.max(maxPlayers, 2), 5),
        roomCode: code,
        createdAt: new Date().toISOString(),
        playerSlots: [{
          uid: state.user.uid, displayName: state.user.displayName || 'Host', seatIndex: 0, connected: true,
        }],
      };
      await set(dbRef(db, `games/${gameId}/meta`), meta);
      await set(dbRef(db, `gameCodes/${code}`), gameId);
      await set(dbRef(db, `userGames/${state.user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });

      await dispatch('bindRemoteGame', gameId);
      return { gameId, code };
    },

    async joinRemoteGame({ state, dispatch }, { code }) {
      trackEvent('room-joined');
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      if (!state.user) throw new Error('Sign in first.');

      const normalized = normalizeRoomCode(code);
      const codeSnap = await get(dbRef(db, `gameCodes/${normalized}`));
      if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
      const gameId = codeSnap.val();

      const metaSnap = await get(dbRef(db, `games/${gameId}/meta`));
      if (!metaSnap.exists()) throw new Error('That game no longer exists.');
      const meta = metaSnap.val();
      if (meta.status !== 'lobby') throw new Error('That game has already started.');

      const slots = meta.playerSlots || [];
      if (!slots.some((s) => s.uid === state.user.uid)) {
        // meta.maxPlayers is absent on rooms created before this cap was
        // configurable - || 2 keeps those behaving exactly as before.
        if (slots.length >= (meta.maxPlayers || 2)) throw new Error('That game is full.');
        slots.push({
          uid: state.user.uid, displayName: state.user.displayName || 'Guest', seatIndex: slots.length, connected: true,
        });
        await update(dbRef(db, `games/${gameId}/meta`), { playerSlots: slots });
        await set(dbRef(db, `userGames/${state.user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
      }

      await dispatch('bindRemoteGame', gameId);
      return { gameId };
    },

    bindRemoteGame({ commit, state }, gameId) {
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      detach('remoteMeta');
      detach('remoteState');
      detach('remotePresence');
      commit('setRemoteGameId', gameId);
      commit('setRemoteError', null);
      commit('setUndoStack', []);

      unsubscribers.remoteMeta = onValue(dbRef(db, `games/${gameId}/meta`), (snap) => {
        commit('setRemoteGameMeta', snap.val());
      });
      unsubscribers.remoteState = onValue(dbRef(db, `games/${gameId}/state`), (snap) => {
        // state is written as a JSON string (see set() calls below) so RTDB
        // never gets a chance to prune empty arrays/objects (e.g. a fresh
        // player's discard: []) out of the tree - it has no native "empty
        // array" representation and silently drops such keys otherwise.
        const raw = snap.val();
        const parsed = raw ? JSON.parse(raw) : null;
        commit('setGameState', parsed);
        // Lifetime stats for the player whose OPPONENT just made the final
        // move - their own submitAction never runs for it. Exactly-once per
        // uid per game via userStats.js's fingerprint transaction.
        if (parsed?.meta?.gameOver) recordFinishedGameOnce(parsed, gameId, state.user);
      });
      unsubscribers.remotePresence = onValue(dbRef(db, `games/${gameId}/presence`), (snap) => {
        commit('setRemotePresence', snap.val());
      });

      // Mark ourselves present, and let Firebase itself clear that the
      // instant this connection actually drops (closed tab, lost network,
      // crash) - far more reliable than any explicit "I'm leaving" write,
      // which a crash or force-quit would never get the chance to send.
      // Skipped if state.user isn't populated yet - only possible on a
      // direct page load/refresh straight into a remote game, where this
      // watcher's `immediate: true` can fire before Firebase Auth has
      // finished restoring the session; presence just won't show for that
      // load (cosmetic-only gap, not worth a full re-bind-on-auth flow).
      if (state.user) {
        const myPresenceRef = dbRef(db, `games/${gameId}/presence/${state.user.uid}`);
        onDisconnect(myPresenceRef).remove();
        set(myPresenceRef, true);
      }
    },

    unbindRemoteGame({ commit, dispatch, state }) {
      if (db && state.remoteGameId && state.user) {
        // Explicit removal for the normal "navigated away cleanly" case -
        // onDisconnect() above is the backstop for everything else, not a
        // replacement for cleaning up now when we already can.
        remove(dbRef(db, `games/${state.remoteGameId}/presence/${state.user.uid}`));
      }
      detach('remoteMeta');
      detach('remoteState');
      detach('remotePresence');
      commit('setRemoteGameId', null);
      commit('setRemoteGameMeta', null);
      commit('setRemotePresence', {});
      commit('setUndoStack', []);
      // Not commit('setGameState', null) - a local game may still be saved
      // (see loadLocalGame) and leaving a remote game shouldn't hide it.
      dispatch('loadLocalGame');
    },

    // Host-only: builds the real GameState from the lobby's seated players
    // and flips the room from 'lobby' to 'in-progress'.
    async startRemoteGame({ state }) {
      if (!db || !state.remoteGameId) return;
      const meta = state.remoteGameMeta;
      if (!meta || meta.hostUid !== state.user?.uid) throw new Error('Only the host can start the game.');
      if ((meta.playerSlots || []).length < 2) throw new Error('Waiting for a second player to join.');

      const playerConfigs = [...meta.playerSlots]
        .sort((a, b) => a.seatIndex - b.seatIndex)
        .map((slot) => ({ id: slot.uid, name: slot.displayName }));
      const seed = Math.floor(Math.random() * 2 ** 31); // host-authoritative seed (see plan)
      const gameState = createGameState({
        playerConfigs,
        boardSide: meta.boardSide,
        seed,
        showLiveScores: Boolean(meta.showLiveScores),
        revealDeckContents: Boolean(meta.revealDeckContents),
        randomizedVillage: Boolean(meta.randomizedVillage),
        synergyPreferredVillage: Boolean(meta.synergyPreferredVillage),
        hardDarkness: Boolean(meta.hardDarkness),
        dungeonDeckTargetValue: meta.dungeonDeckTargetValue ?? null,
        bearerCount: meta.bearerCount || 1,
        bearerNeverEscapes: Boolean(meta.bearerNeverEscapes),
        // meta.autoEndTurn ?? true, not Boolean(meta.autoEndTurn) - a room
        // created before this field existed has no autoEndTurn key at all
        // (undefined), and that should behave like every game always has
        // (auto-end-turn ON), not silently flip off for old rooms.
        autoEndTurn: meta.autoEndTurn ?? true,
        // Matt asked for this directly, twice: whoever creates the room
        // always seats at seatIndex 0 (see playerConfigs's own sort just
        // above), so without this the host would always take the first
        // turn too - see createGameState's own comment.
        randomizeStartingPlayer: true,
      });

      await set(dbRef(db, `games/${state.remoteGameId}/state`), JSON.stringify(gameState));
      await update(dbRef(db, `games/${state.remoteGameId}/meta`), {
        status: 'in-progress',
        lastActiveAt: Date.now(),
        // An async room says who it waits on from the first turn (asyncPlay.js).
        ...(state.remoteGameMeta?.async ? waitingPatch(gameState, state.remoteGameMeta, asyncAdapter) : {}),
      });
    },

    // Lobby listing: the current user's remote games (host or guest), newest
    // first. Not committed to Vuex state - Landing.vue owns the fetched list
    // locally since it's a one-shot lobby-screen concern, not something the
    // rest of the app needs to react to.
    async fetchUserGames({ state }) {
      if (!db || !state.user) return [];
      const indexSnap = await get(dbRef(db, `userGames/${state.user.uid}`));
      const index = indexSnap.val() || {};
      const gameIds = Object.keys(index);
      const games = await Promise.all(gameIds.map(async (gameId) => {
        const metaSnap = await get(dbRef(db, `games/${gameId}/meta`));
        return metaSnap.exists() ? { gameId, ...index[gameId], meta: metaSnap.val() } : null;
      }));
      return games
        .filter(Boolean)
        .sort((a, b) => new Date(b.meta.createdAt || 0) - new Date(a.meta.createdAt || 0));
    },

    // Removes a room from *this player's own* lobby list only - deliberately
    // not a full room teardown (games/{gameId}/meta+state, gameCodes/{code}).
    // A stuck/interrupted/no-longer-wanted room should stop showing up for
    // the player who's done with it without silently pulling the rug out
    // from under the other player, who might still have it open or want to
    // resume it. The room and its code stay valid - reachable again via
    // joinRemoteGame if the removing player ever gets the code back.
    async removeUserGame({ state }, gameId) {
      if (!db || !state.user) return;
      await remove(dbRef(db, `userGames/${state.user.uid}/${gameId}`));
    },

    // ---- Shared by both local and remote play ------------------------------

    async submitAction({ state, commit }, action) {
      const prevState = state.gameState;
      let nextState = engineDispatch(prevState, action);
      // Matt: "The Hellhound has a battle effect that says each other
      // player must discard a hero... it asks the player to decide which
      // hero the computer discards. But the computer should choose for
      // itself." Any pending choice this action just queued for an AI
      // opponent (not necessarily the active player - see
      // heuristicBot.js's resolveAIOwedPendingChoices) gets decided right
      // here, before anything renders, so it never reaches
      // PlayerDock.vue's hotseat-sharing fallback (correct for a second
      // HUMAN sharing the screen, wrong for an AI). Folded into the SAME
      // nextState the undo-stack logic below snapshots, so undoing the
      // human's action also undoes its AI-choice consequence in one step.
      nextState = resolveAIOwedPendingChoices(nextState);

      // Undo is only meaningful for "my still-in-progress turn": once the
      // active player changes (however it happened - the four explicit
      // end-turn action types, or a card ability like King Caelan's Writ
      // that ends the turn as a side effect via meta.endTurnOverride) or the
      // game ends, there's no longer a "this turn" to undo back into. The
      // other lock condition - revealsNewInformation - is what makes undo
      // safe rather than a way to reroll draws; see
      // engine/state/informationReveal.js for why.
      // Rulebook: choosing a monster commits you to fighting it - you can't
      // back out for a different one once you've seen its Battle effect
      // wants an unfavorable discard, even though (with resolveCombat.js's
      // own pause-on-a-Battle-queued-choice fix) no HIDDEN information has
      // technically been revealed yet at this exact point. Without this,
      // revealsNewInformation alone would let a player undo straight back
      // past a real commitment: CHOOSE_MONSTER paused mid-resolution
      // (hall refill/outcome deferred to resumeCombatIfReady, once the
      // Battle choice is actually answered) looks identical to "nothing
      // happened yet" from a pure hidden-vs-visible standpoint, but the
      // commitment itself already happened.
      const turnPassed = nextState.meta.activePlayerIndex !== prevState.meta.activePlayerIndex
        || nextState.meta.gameOver;
      const combatCommittedButPaused = Boolean(nextState.meta.pendingCombatContinuation);
      if (turnPassed || combatCommittedButPaused || revealsNewInformation(prevState, nextState)) {
        commit('setUndoStack', []);
      } else {
        commit('setUndoStack', [...state.undoStack, prevState]);
      }

      const persist = async (s) => {
        commit('setGameState', s);
        if (state.remoteGameId) {
          if (!db) throw new Error('Firebase is not configured (see .env.example).');
          // Optimistic local commit for snappy UX; the RTDB listener will
          // echo the same state back and re-commit it (a harmless no-op
          // diff).
          await set(dbRef(db, `games/${state.remoteGameId}/state`), JSON.stringify(s));
          recordWaiting(state.remoteGameId, s, state.remoteGameMeta);
        } else {
          writeLocalGame(s);
        }
      };

      // Matt: "I just clicked to do a village turn and for some reason I
      // drew a longspear and destroyed a shrouded cadaver. I don't
      // understand what just happened." That was the Computer's whole turn
      // - previously runAiTurnsToNextHuman ran synchronously right after the
      // human's own action, in the same tick, so the "{{ activePlayer.name
      // }} is taking its turn..." banner (GameBoard.vue) never actually got
      // painted: the browser only ever rendered the *end* result. Committing
      // the human's own action first, then awaiting a short pause before
      // (and between) each AI turn, forces a real repaint in between, so the
      // banner and each turn's board/log changes are actually visible
      // instead of an instant, unexplained jump.
      await persist(nextState);
      while (!nextState.meta.gameOver) {
        const active = nextState.players[nextState.meta.activePlayerIndex];
        if (!active.isAI) break;
        await new Promise((resolve) => { setTimeout(resolve, 700); });
        nextState = playAITurn(nextState, active.id, aiWeightsFor(active));
        await persist(nextState);
      }
      // Matt: "It would be great if we tracked these stats (plus win/loss)
      // per user. So I could look up my all time stats." Fire-and-forget,
      // exactly-once per game per uid - see userStats.js.
      if (nextState.meta.gameOver) recordFinishedGameOnce(nextState, state.remoteGameId, state.user);
    },

    // Pops the most recent pre-action snapshot and restores it wholesale -
    // the same "write the entire GameState" path submitAction already uses,
    // so remote sync (or lack thereof, locally) needs no special handling.
    async undoLastAction({ state, commit }) {
      if (!state.undoStack.length) return;
      const restored = state.undoStack[state.undoStack.length - 1];
      commit('setUndoStack', state.undoStack.slice(0, -1));
      commit('setGameState', restored);
      if (state.remoteGameId) {
        if (!db) throw new Error('Firebase is not configured (see .env.example).');
        await set(dbRef(db, `games/${state.remoteGameId}/state`), JSON.stringify(restored));
        recordWaiting(state.remoteGameId, restored, state.remoteGameMeta);
      } else {
        writeLocalGame(restored);
      }
    },
  },
});
